// En: solicitudes.component.ts (Angular)

import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild, OnDestroy, AfterViewInit, ElementRef } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms'; // <-- Imports añadidos
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorModule, MatPaginatorIntl } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSelectModule } from '@angular/material/select'; // <-- Import añadido
import { ActivatedRoute, Router } from '@angular/router';
import { SolicitarVisitaService, PagedRequestOptions } from 'src/app/services/solicitar-visita.service';
import { StorageService } from 'src/app/services/storage.service';
import { ClientesService } from 'src/app/services/clientes.service';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';
import { Subscription, Subject, merge, of as observableOf } from 'rxjs';
import { debounceTime, distinctUntilChanged, startWith, catchError, take } from 'rxjs/operators';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { PaginatorIntl } from 'src/app/paginator-intl';
import { SharedModule } from '@app/shared/shared.module';
import { TipoServicioService } from '@app/services/tipo-servicio.service';

@Component({
  selector: 'app-solicitudes',
  standalone: true,
  imports: [
    CommonModule, MatCardModule, MatFormFieldModule, MatInputModule, MatButtonModule,
    MatTableModule, MatSortModule, MatPaginatorModule, MatIconModule, MatTooltipModule,
    ReactiveFormsModule, MatProgressSpinnerModule, MatSelectModule,
    SharedModule
  ],
  templateUrl: './solicitudes.component.html',
  styleUrls: ['./solicitudes.component.scss'],
  providers: [
    { provide: MatPaginatorIntl, useClass: PaginatorIntl }
  ]
})
export class SolicitudesComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild('input') input!: ElementRef;
  
  searchForm: FormGroup; // <-- Se reintroduce el FormGroup
  loading = true;
  
  dataSource = new MatTableDataSource<any>();
  baseDisplayedColumns: string[] = [
    'id', 
    'fechaIngreso',
    'local', 
    'zona',
    'especialidad',
    'tipoServicio', 
    'fechaVisita', 
    'tecnico',
    'tipoMantenimiento', 
    'observaciones',
    'estado', 
    'mesFacturacion', 
    'acciones'
  ];

  displayedColumns: string[] = [...this.baseDisplayedColumns];

  totalItems = 0;
  selectedServiceType: string = '';
  tiposServicio: any[] = [];
  selectedCompany: any = null;
  public isGruman: boolean = false;
  public clientes: any[] = [];
  public selectedClientId: number | null = null;

  meses = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
  anios: number[] = [];
  selectedMes: string = '';
  selectedAnio: string = '';

  private companySubscription: Subscription | null = null;
  private searchSubject = new Subject<string>();

  constructor(
    private fb: FormBuilder, // <-- Se inyecta FormBuilder
    private router: Router,
    private route: ActivatedRoute,
    private storageService: StorageService,
    private solicitarVisitaService: SolicitarVisitaService,
    private clientesService: ClientesService,
    private tipoServicioService: TipoServicioService,
  ) {}

  ngOnInit(): void {
    this.generarAnios();
    this.initForm();
    this.getTiposServicio();
   
    if (this.isGruman) {
        this.loadClientes();
    }
    
    // Capturar si venimos desde el Dashboard con un mes específico
    this.route.queryParams.subscribe((params: any) => {
      if (params['mesFacturacion']) {
        const parts = params['mesFacturacion'].split(' ');
        if (parts.length === 2) {
          this.selectedMes = parts[0];
          this.selectedAnio = parts[1];
        }
      }
    });

    this.searchSubject.pipe(
      debounceTime(400),
      distinctUntilChanged()
    ).subscribe(() => {
      this.paginator.pageIndex = 0;
      this.loadSolicitudes();
    });

    this.companySubscription = this.storageService.companyChange$.subscribe((company: any) => {
      this.selectedCompany = company;
      
      this.isGruman = this.selectedCompany?.nombre?.trim().toUpperCase() === 'GRUMAN';
      this.selectedClientId = null;
      if (this.isGruman) {
        this.loadClientes();
      }

      this.updateDisplayedColumns(this.selectedCompany);

      if (this.paginator) {
        this.paginator.pageIndex = 0;
        this.loadSolicitudes();
      }

      const user = this.storageService.getItem('currentUser');
      if (user && user.selectedCompany) {      
        this.selectedCompany = user.selectedCompany;
        // 🛠️ FIX: Inicializar el filtro de cliente en la carga si es GRUMAN
        this.isGruman = this.selectedCompany.nombre?.trim().toUpperCase() === 'GRUMAN';
        this.updateDisplayedColumns(this.selectedCompany);
        if (this.isGruman) {
          this.loadClientes();
        }
      } else {      
        this.updateDisplayedColumns(null);
      }
    });
  }

  // Cargar catálogo de clientes para el dropdown
  loadClientes(): void {
    this.clientesService.getClientesWithGruman().subscribe({
      next: (clientes: any[]) => this.clientes = clientes || [],
      error: (err: any) => console.error('Error al cargar clientes:', err)
    });
  }

  // Evento al seleccionar un cliente en la vista GRUMAN
  onClientFilterChange(): void {
    if (this.paginator) this.paginator.pageIndex = 0;
    this.loadSolicitudes();
  }
  
  ngAfterViewInit() {
    merge(this.sort.sortChange, this.paginator.page)
      .pipe(startWith({}))
      .subscribe(() => this.loadSolicitudes());
  }

  ngOnDestroy() {
    if (this.companySubscription) {
      this.companySubscription.unsubscribe();
    }
  }

  generarAnios(): void {
    const currentYear = new Date().getFullYear();
    for (let y = 2023; y <= currentYear + 2; y++) {
      this.anios.push(y);
    }
  }

  onDateFilterChange() {
    if (this.paginator) this.paginator.pageIndex = 0;
    this.loadSolicitudes();
  }

  getTiposServicio(): void {
    this.tipoServicioService.findAll().subscribe({
      next: (data: any) => {
        this.tiposServicio = data;
      },
      error: (err: any) => console.error('Error cargando tipos de servicio', err)
    });
  }

  private initForm(): void {
    this.searchForm = this.fb.group({
      clienteId: [''],
    });
  }

  private updateDisplayedColumns(company: any) {
    const newCols = [...this.baseDisplayedColumns];
    let esGruman = false;    
    
    if (company && company.nombre) {      
      const nombreLimpio = company.nombre.trim().toUpperCase();
      if (nombreLimpio === 'GRUMAN') {
        esGruman = true;
      }
    }

    if (esGruman) {
      newCols.splice(2, 0, 'cliente');      
    }

    this.displayedColumns = newCols;
  }

  loadSolicitudes(): void {
    this.loading = true;

    let mesFacturacionStr = '';
    if (this.selectedMes) mesFacturacionStr += this.selectedMes;
    if (this.selectedAnio) mesFacturacionStr += (mesFacturacionStr ? ' ' : '') + this.selectedAnio;
    
    const options: PagedRequestOptions = {
      page: this.paginator.pageIndex + 1,
      limit: this.paginator.pageSize,
      searchTerm: this.input?.nativeElement.value || '',
      serviceType: this.selectedServiceType,
      sortBy: this.sort.active || 'id',
      sortOrder: this.sort.direction || 'desc',
      clientId: this.searchForm.get('clienteId')?.value || undefined,
      mesFacturacion: mesFacturacionStr || undefined
    };

    if (this.isGruman) {
      if (this.selectedClientId) {
        options.clientId = this.selectedClientId;
      }
    } else if (this.selectedCompany?.id) {
      options.clientId = this.selectedCompany.id;
    }

    this.solicitarVisitaService.getSolicitudesVisitaMultifiltro(options)
    .pipe(
      catchError(() => observableOf(null))
    ).subscribe((response: any) => {
      this.dataSource.data = response ? response.data : [];
      this.totalItems = response ? response.total : 0;
      this.loading = false;
    });
  }

  applyFilter() {
    this.searchSubject.next(this.input.nativeElement.value);
  }

  onServiceTypeChange() {
    this.paginator.pageIndex = 0; // Volvemos a la página 1
    this.loadSolicitudes();       // Recargamos la tabla
  }

  resetFilters(): void {
    this.searchForm.reset({ clienteId: '' });
    if (this.input) this.input.nativeElement.value = '';
    this.selectedServiceType = '';
    this.selectedClientId = null;
    this.paginator.pageIndex = 0;
    this.sort.active = 'id';
    this.sort.direction = 'desc';
    this.selectedMes = '';
    this.selectedAnio = '';
    this.getTiposServicio();
    this.loadSolicitudes();
  }

 
  verDetalle(solicitud: any): void {
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/transacciones/solicitudes-de-visita/ver-solicitud/pendiente', solicitud.id])
    );
    window.open(url, '_blank');
  }

  exportarExcel(): void {
    Swal.fire({
      title: 'Generando exportación...', 
      text: 'Esto puede tardar unos segundos. El archivo se descargará automáticamente.',
      allowOutsideClick: false, 
      didOpen: () => Swal.showLoading()
    });

    let mesFacturacionStr = '';
    if (this.selectedMes) mesFacturacionStr += this.selectedMes;
    if (this.selectedAnio) mesFacturacionStr += (mesFacturacionStr ? ' ' : '') + this.selectedAnio;

    // Construimos las opciones con los filtros actuales de la vista
    const options: PagedRequestOptions = {
      searchTerm: this.input?.nativeElement.value || '',
      serviceType: this.selectedServiceType,
      sortBy: this.sort.active || 'id',
      sortOrder: this.sort.direction || 'desc',
      clientId: this.searchForm.get('clienteId')?.value || undefined,
      status: this.searchForm.get('status')?.value || undefined,
      tipoMantenimiento: this.searchForm.get('tipoMantenimiento')?.value || undefined,
      mesFacturacion: mesFacturacionStr || undefined,
      fechaDesde: this.searchForm.get('fechaDesde')?.value || undefined,
      fechaHasta: this.searchForm.get('fechaHasta')?.value || undefined,
    };
    
    if (this.isGruman) {
      if (this.selectedClientId) {
        options.clientId = this.selectedClientId;
      }
    } else if (this.selectedCompany?.id) {
      options.clientId = this.selectedCompany.id;
    }

    // Llamamos al método del servicio que maneja Blobs
    this.solicitarVisitaService.descargarExcelSolicitudes(options).subscribe({
      next: (response: any) => {
        this.solicitarVisitaService.exportarExcelMaestro(response.data, 'General-Visitas');
      },
      error: (err: any) => {
        console.error(err);
        Swal.close();
        Swal.fire('Error', 'No se pudo obtener los datos del servidor.', 'error');
      }
    });
  }

  // --- Helpers para la vista ---
  getTipoMantenimientoLabel = (tipo: string) => ({ 'correctivo': 'Correctivo', 'preventivo': 'Preventivo', 'scheduled': 'Programado' }[tipo] || tipo);
  getEstadoLabel = (estado: string) => ({ 'pending': 'Pendiente', 'approved': 'Aprobada', 'rejected': 'Rechazada', 'validated': 'Validada', 'finalized': 'Finalizada' }[estado] || estado);
  getTipoMantenimientoClass = (tipo: string) => ({ 'correctivo': 'badge badge-danger', 'preventivo': 'badge badge-warning', 'scheduled': 'badge badge-info' }[tipo] || 'badge badge-secondary');
  getEstadoClass = (estado: string) => ({ 'pending': 'badge badge-warning', 'approved': 'badge badge-primary', 'rejected': 'badge badge-danger', 'validated': 'badge badge-success', 'finalized': 'badge badge-info' }[estado] || 'badge badge-secondary');
}